import { Schema } from "effect"
import { Rpc, RpcGroup } from "effect/rpc"
import { AgentMessage } from "./agent-message"
import { Command, EventRef } from "./commands/index"
import { Principal, Tier } from "./common"
import { WakeBatch, WakeReason } from "./domain/agentlife"
import { MemoryItem, MemoryScope, MemoryType } from "./domain/knowledge"
import { Agent } from "./domain/org"
import { GithubRepo, GitSha } from "./github"
import {
  AgentId,
  ArtifactId,
  Epoch,
  IntegrationId,
  ItemId,
  Markdown,
  MergeId,
  Millis,
  NonNegInt,
  PosInt,
  ProjectId,
  ProposalId,
  RepoId,
  RunId,
  RunnerId,
  ShortRef,
  Slug,
  UserId,
  WorkerRef,
  WorkspaceId,
} from "./ids"
import { ContextPack, CorrelationId, JobTemplate } from "./job"
import { Placement } from "./placement"
import { RateLimitReset, Run, RunError, RunKind, SessionRef, StopReason } from "./run"

// #region internal
/**
 * DO ↔ DO messages (§2.2). Facts travel through a transactional outbox: the row is written in the same transaction
 * as the event that caused it, an alarm drains it, and the receiver dedupes on `${log}:${seq}:${index}`
 * (inbox_dedupe). Delivery is at-least-once; every effect happens once. The caller's identity rides on the internal
 * call and becomes the actor; nothing in a payload can claim one. Reads across DOs are plain RPC, never outboxed.
 */
export const OutboxMeta = {
  /** The event that caused this message, in the sender's log. */
  source: EventRef,
  /** Position among the messages that event caused (one event can fan out). */
  index: NonNegInt,
} as const

/**
 * Decision 0059: one PR artifact to watch, as the Project DO lists it (core `watchEntries`). No hash and no change
 * time: the hub owns `last_hash` / `changed_at` and a `PrWatchChanged` never overwrites them.
 */
export const PrWatchEntry = Schema.Struct({
  artifactId: ArtifactId,
  itemId: Schema.NullOr(ItemId),
  url: Schema.String,
  repo: GithubRepo,
  number: PosInt,
  /** The run principal whose runners and gh login watch it. */
  ownerId: UserId,
  openerRunnerId: Schema.NullOr(RunnerId),
  repoId: Schema.NullOr(RepoId),
  hot: Schema.Boolean,
  createdAt: Millis,
  /** Wave E (decision 0063): the item's lend at watch time; watchers follow `mayRun`. Absent = none. */
  lentRunnerId: Schema.optionalKey(Schema.NullOr(RunnerId)),
})
export type PrWatchEntry = typeof PrWatchEntry.Type

/** Project → Workspace DO (the runner hub, workspace memory, the project index). */
export const WorkspaceInbound = Schema.TaggedUnion({
  /** Queue a runner run at the hub, with its frozen job template (C3: stored with the run, returned by claim). */
  Enqueue: {
    ...OutboxMeta,
    runId: RunId,
    projectId: ProjectId,
    kind: RunKind,
    ownerId: UserId,
    placement: Placement,
    job: JobTemplate,
    /**
     * Display only (H8 `leases`), not part of the signed job (fix wave §4.4). D7 (decision 0039): the hub's names
     * `taskRef`/`taskTitle` stay this release; they hold the item's ref and title.
     */
    label: Schema.optionalKey(
      Schema.Struct({
        taskRef: Schema.NullOr(ShortRef),
        taskTitle: Schema.NullOr(Schema.String),
        /** Settings wave: the temporary teammate ("W-3"), shown as H8 `leases[].workerRef`. */
        workerRef: Schema.optionalKey(WorkerRef),
      }),
    ),
    /**
     * Wave E (decision 0063): the run's item (a validator's subject item), and that item's lend when queued. The hub's
     * lease re-check (`mayRun`) reads them. Absent on rows from before E = no item, no lend.
     */
    itemId: Schema.optionalKey(Schema.NullOr(ItemId)),
    lentRunnerId: Schema.optionalKey(Schema.NullOr(RunnerId)),
    /**
     * Decision 0128: the person who asked for a turn (its `Run.askedBy` when a person). Placement runs a turn asked by
     * someone other than the keeper on the asker's own or a team subscription. Absent = the keeper (or no person).
     */
    turnAsker: Schema.optionalKey(UserId),
    /**
     * Decision 0165: the item's command check that looks heavy (core `heavyCheckOf`), sent only when nothing routes the
     * run to a runner or profile. The hub then prefers the profile with the bigger sandbox inside a runner. Cloud
     * internal (Project DO → Workspace DO); absent = not heavy.
     */
    heavyCheck: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(200))),
  },
  /**
   * Wave E (decision 0063): an item's lend changed (`ItemUpdated` with `lentRunnerId`). Sent on the per-run lanes for
   * every queued, offered or leased run of the item. A leased run keeps its lease; the lend decides the next attempt.
   */
  LendChanged: {
    ...OutboxMeta,
    projectId: ProjectId,
    itemId: ItemId,
    runIds: Schema.Array(RunId),
    lentRunnerId: Schema.NullOr(RunnerId),
  },
  Cancel: { ...OutboxMeta, runId: RunId, reason: Schema.String },
  ApplyMemory: { ...OutboxMeta, item: MemoryItem }, // an approved workspace-scope memory proposal
  ProjectIndexChanged: {
    ...OutboxMeta,
    projectId: ProjectId,
    name: Schema.String,
    status: Schema.Literals(["active", "paused", "archived"]),
  },
  /** Decision 0059: the project's PRs to watch, whole (replaces the hub's rows for this project). */
  PrWatchChanged: {
    ...OutboxMeta,
    projectId: ProjectId,
    entries: Schema.Array(PrWatchEntry).check(Schema.isMaxLength(200)),
  },
  /**
   * Decision 0166 §4: the project's live agents, whole (replaces the hub's agent-name rows for this project), so names and
   * handles can be checked across the workspace. Sent when an agent is hired, renamed or retired.
   */
  AgentNamesChanged: {
    ...OutboxMeta,
    projectId: ProjectId,
    agents: Schema.Array(
      Schema.Struct({ agentId: AgentId, name: Schema.String, handle: Schema.String }),
    ).check(Schema.isMaxLength(500)),
  },
  /** Decision 0059: a person chose Merge: the hub signs and delivers it. */
  Merge: {
    ...OutboxMeta,
    mergeId: MergeId,
    projectId: ProjectId,
    artifactId: ArtifactId,
    url: Schema.String,
    repo: GithubRepo,
    number: PosInt,
    headSha: GitSha,
    ownerId: UserId,
    openerRunnerId: Schema.NullOr(RunnerId),
    repoId: Schema.NullOr(RepoId),
    notAfter: Millis,
  },
})
export type WorkspaceInbound = typeof WorkspaceInbound.Type

/**
 * Into a Project DO: the hub's run facts (RecordRunWaiting / Leased / Requeued / Started / Milestone / Ended, actor
 * `runner` or `system:lease_reaper`), the Workspace's SyncWorkspace, an Agent DO's platform-turn facts and
 * OpenProposal{permission}, a Run DO's OpenProposal{permission} and stall signals. Decided like any command.
 */
export const ProjectInbound = Schema.Struct({ ...OutboxMeta, command: Schema.suspend(() => Command) })
export type ProjectInbound = typeof ProjectInbound.Type

/** hub → the run's stream sink (its Run DO, for every runner run, W2): refuse frames of this epoch from now on. */
export const RunSinkInbound = Schema.TaggedUnion({
  LeaseClosed: {
    ...OutboxMeta,
    runId: RunId,
    epoch: Epoch,
    reason: Schema.Literals(["final", "released", "expired", "revoked", "stale"]),
  },
})

/** permission.response to the run's sink, bound to the prompt it answers (inputHash). */
const PermissionDecision = {
  ...OutboxMeta,
  runId: RunId,
  requestId: Schema.String,
  inputHash: Schema.String,
  allow: Schema.Boolean,
  /** Settings wave §4.5: "this_turn" = allow this tool for the rest of the run (from approve{remember: "run"}).
   *  "always" (decision 0053, from approve{remember: "always"}) = Codex's own exec-policy rule; the Run DO forwards it
   *  only for a prompt that offered it. The Agent DO ignores it. Absent = "once". */
  remember: Schema.optionalKey(Schema.Literals(["once", "this_turn", "always"])),
} as const

/** Project → Agent DO (the body). PermissionDecision goes to the Run DO this wave (W2). */
export const AgentInbound = Schema.TaggedUnion({
  Wake: {
    ...OutboxMeta,
    agentId: AgentId,
    reason: WakeReason,
    key: Schema.String,
    cause: EventRef,
    /**
     * Wave E (decision 0063, spec §4.3): whose batch this wake joins (its item's or run's asker; the agent for its own
     * work), set by the Project in a shared project only. Absent = it joins whichever batch is taken (today's rule).
     */
    askedBy: Schema.optionalKey(Principal),
    /**
     * Decision 0092: the message another agent sent with this wake (an ask, an answer, an @mention), for the
     * recipient's chat. Absent for every other wake, and when the Project no longer has the comment's text.
     */
    message: Schema.optionalKey(AgentMessage),
  },
  PushAgentConfig: { ...OutboxMeta, agent: Agent }, // → AgentConfigSynced
  PermissionDecision,
  /** Every RunEnded of one of this agent's turn runs, whatever the reason (W17): the body never waits on a dead turn. */
  TurnRunEnded: {
    ...OutboxMeta,
    agentId: AgentId,
    runId: RunId,
    stopReason: StopReason,
    /**
     * Incident 2026-10-15: a `rate_limited` turn's earliest known reset (from its last waiting reasons), for the
     * asker's notice ("Rate limited until 06:40"). Absent: not rate limited, or no reset known.
     */
    resetsAt: Schema.optionalKey(Millis),
    /** Decision 0155: which limit ran out (`5h`, `24h`, `7d`), when known: the notice names it. */
    limitWindow: Schema.optionalKey(RateLimitReset.fields.window),
    /** Decision 0135: the run's error (the body's chat notice says what failed and what happens next). */
    error: Schema.optionalKey(RunError),
    /** Decision 0135: the run started before it ended (a sandbox or MCP failure before the start is retried once). */
    started: Schema.optionalKey(Schema.Boolean),
  },
  StopTurn: { ...OutboxMeta, agentId: AgentId, runId: RunId, reason: Schema.String },
})
export type AgentInbound = typeof AgentInbound.Type

/**
 * Project → Run DO. Prepare is sent for EVERY runner run (turns included, W2); for a turn `brief` is "" and the
 * body's prepareTurn adds the messages. The handoff (previous attempt's `next`, validator findings) is in the pack.
 */
export const RunInbound = Schema.TaggedUnion({
  Steer: {
    ...OutboxMeta,
    runId: RunId,
    text: Schema.String,
    /** Decision 0101: `next` waits for the current tool call (comments); absent = `now` (work controls). */
    priority: Schema.optionalKey(Schema.Literals(["now", "next", "later"])),
  },
  Prepare: {
    ...OutboxMeta,
    run: Run,
    packRef: Schema.String,
    brief: Markdown,
    workspaceId: WorkspaceId,
    projectId: ProjectId,
    /** Limits and model for turn.start. */
    job: JobTemplate,
    /** Workers: the previous attempt's sessionOut; validators and turns: null. */
    sessionIn: Schema.NullOr(SessionRef),
  },
  PermissionDecision,
})
export type RunInbound = typeof RunInbound.Type

/** Project → User DO. */
export const UserInbound = Schema.TaggedUnion({
  Notify: {
    ...OutboxMeta,
    userId: UserId,
    projectId: ProjectId,
    proposalId: Schema.NullOr(ProposalId),
    tier: Tier,
  },
  /** A card resolved elsewhere closes itself (doneAt). */
  ResolveNotification: { ...OutboxMeta, userId: UserId, proposalId: ProposalId },
  ApplyMemory: { ...OutboxMeta, item: MemoryItem }, // an approved user-scope memory proposal
})

/** Platform-mind graph tools: the same tool layer the MCP endpoint serves (W7). */
export const GraphToolRequest = Schema.TaggedUnion({
  list: { runId: RunId },
  call: { runId: RunId, toolCallId: Schema.String, name: Schema.String, input: Schema.Unknown },
})
export const GraphToolDescriptor = Schema.Struct({
  name: Schema.String,
  description: Schema.String,
  inputSchema: Schema.JsonObject,
})
export const GraphToolResponse = Schema.TaggedUnion({
  list: { tools: Schema.Array(GraphToolDescriptor) },
  call: { isError: Schema.Boolean, output: Schema.Json },
})

/** = the RequestTurn payload of ProjectInternalRpcs. The body mints runId (§3.1). Decoded through `decodeStored`. */
/**
 * Decision 0088 addendum: what a cloud mind's history already has of the volatile pages (cloud-internal: body →
 * Project, never on the runner wire). `baseline`: nothing (first turn, a compaction or a cleared chat since); else
 * `seen` is the page version (artifact id) by path its earlier turns loaded.
 */
export const TurnPagesReport = Schema.Struct({
  baseline: Schema.Boolean,
  seen: Schema.Array(Schema.Struct({ path: Schema.String, artifactId: Schema.String })),
})

export const RequestTurnPayload = Schema.Struct({
  runId: RunId,
  agentId: AgentId,
  wakeBatch: WakeBatch,
  /** Used only for a platform mind; missing (an older body): every volatile page in full. */
  pages: Schema.optionalKey(TurnPagesReport),
  /**
   * Decision 0135 (lesson 3): the agent's previous turn ended with a question to a person in prose. The Project adds
   * one line to this turn's file when that turn opened no card.
   */
  askedInProse: Schema.optionalKey(RunId),
  /**
   * Decision 0143: the turn's correlation id (`msg:<messageId>` of the person's message it answers), stamped on the
   * turn's RequestRun so the run, its job and its logs carry it. Missing (an older body): `run:<runId>` as before.
   */
  correlationId: Schema.optionalKey(CorrelationId),
})
export type RequestTurnPayload = typeof RequestTurnPayload.Type

/**
 * Agent DO → Project DO, RPC: start a turn run (the reply always carries the assembled pack this wave: runner-mind
 * turns need it too, W2), and the platform-mind graph tools.
 */
export class ProjectInternalRpcs extends RpcGroup.make(
  Rpc.make("RequestTurn", {
    payload: RequestTurnPayload.fields,
    success: Schema.Struct({ run: Run, pack: Schema.NullOr(ContextPack) }),
  }),
  Rpc.make("CallTool", { payload: GraphToolRequest, success: GraphToolResponse }),
) {}

/** Project → User / Workspace / Agent DO, RPC (read only): memory for context assembly. */
export const MemoryFilter = Schema.Struct({
  scope: MemoryScope,
  types: Schema.Array(MemoryType), // empty = all loadable types
  tags: Schema.Array(Slug),
  paths: Schema.Array(Schema.String),
  integrations: Schema.Array(IntegrationId),
})
export class MemoryStoreRpcs extends RpcGroup.make(
  Rpc.make("GetMemory", { payload: MemoryFilter, success: Schema.Array(MemoryItem) }),
) {}
// #endregion internal
